import type { Tutorial } from "@app/analytics/types";
import type {
  BillingCadence,
  BillingPlan,
  UserSettings,
} from "@app/common/types";
import type {
  CalendarProviderId,
  SSOProviderId,
} from "@app/common/types/providerTypes";
import Config from "@app/config";
import { sendEvent } from "@app/utils/tracking/Tracker";

import type {
  UpdateCreateWorkspaceSetting,
  UpdateCreateWorkspaceSettings,
} from "../account/WorkspaceSettings/WorkspaceWizard/types";
import { AddReadUILocation } from "../types/trackingEventTypes";

export const addReadToLiveMeeting = sendEvent<{
  uiLocation: AddReadUILocation;
}>("add_read_to_live_meeting");

export const connectCalendar = sendEvent<{
  stage: "start" | "finish" | "decline";
  provider: CalendarProviderId;
}>("connect_calendar");

export const deleteProvider = sendEvent<{
  providerId: SSOProviderId | CalendarProviderId;
}>("delete_provider");

export const firstTimeSettingsClick = sendEvent<{ setting: string }>(
  "first_time_settings_click"
);

export const inviteRead = sendEvent<{
  inviteNumAttendees?: number;
  inviteAction: "add" | "remove" | "unknown";
  inviteMethod: "copy_link" | "direct";
}>("invite_read");

// GA Recommended Events
// https://developers.google.com/analytics/devguides/collection/ga4/reference/events
export const signIn = sendEvent<{
  method: SSOProviderId | "email" | "unknown";
}>("login");

export const signUp = sendEvent<{
  method: SSOProviderId | "email" | "unknown";
}>("sign_up");

export const tutorialBegin = sendEvent<{ tutorial: Tutorial }>(
  "tutorial_begin"
);
export const tutorialEnd = sendEvent<{ tutorial: Tutorial; step?: number }>(
  "tutorial_end"
);

export const optOut = sendEvent("opt_out");

export const acceptRecommendation = sendEvent("accept_recommendation");
export const rejectRecommendation = sendEvent("reject_recommendation");

export const changeUserSettings = sendEvent<Partial<UserSettings>>(
  "change_user_settings",
  { statsig: true }
);
export const changeMeetingListView = sendEvent<{ view: string }>(
  "change_meeting_list_view"
);

export const searchMeetings = sendEvent<{ term: string }>("search_meetings");

export const addRss = sendEvent<{
  browser: "Chrome" | "Edge";
  source: "modal" | "banner" | "onboarding";
}>("add_rss");

export const downloadTranscript = sendEvent("download_transcript");
export const downloadSummary = sendEvent("download_summary");
export const downloadTrailer = sendEvent("download_trailer");
export const downloadHighlights = sendEvent("download_highlights");
export const downloadRecording = sendEvent("download_recording");

export const requestAccess = sendEvent<{ reportType: "meeting" | "topic" }>(
  "request_access"
);

export const playCoachingMoment = sendEvent<{
  type: "filler_words" | "wpm" | "non_inclusive_terms" | "interruptions";
  id: string;
}>("play_coaching_moment");
export const clickBlockOrMarker = sendEvent<{ id: string; label: string }>(
  "click_block_or_marker"
);

export const editName = sendEvent<{ entity: "workspace" | "team" | "meeting" }>(
  "edit_name"
);

export const deleteReport = sendEvent<{ reportType: "meeting" | "readout" }>(
  "delete_report"
);
export const skipForNow = sendEvent("skip_for_now", {
  statsig: true,
});

export const logMsg = ({ msg }: { msg: string }) => {
  if (Config.tracking.MIXPANEL_VERBOSE_LOG) {
    sendEvent<{ msg: string }>("log_msg")({ msg });
  }
};

export const longWait = sendEvent<{ page: string; duration: number }>(
  "long_wait"
);

// Used when we run experiments, may not be in use
// but could be useful in the future
export const startExperiment = sendEvent<{
  experiment: string;
  bucket: number;
  reason?: string;
}>("experiment_start");

export const authEvent = ({
  action,
  method,
}: {
  action: string;
  method: SSOProviderId | "email" | "unknown";
}) => {
  switch (action) {
    case "signup":
      return signUp({ method });
    case "signin":
      return signIn({ method });
    default:
      return sendEvent("unknown_auth_event")({ action, method });
  }
};

export const copyToClipboard = sendEvent<{ name: string }>("copy_to_clipboard");

/**
 * Billing events
 * */
export const activatedFreeTrial = sendEvent<{ action?: string }>(
  "activated_free_trial"
);

export const addPaymentMethod = sendEvent("add_payment_method");
export const removePaymentMethod = sendEvent("remove_payment_method");
export const updateBillingInfo = sendEvent("update_billing_info");
export const initiateCheckoutError = sendEvent("initiate_checkout_error");
export const purchasePlan = sendEvent<{
  seats: number;
  plan: BillingPlan;
  cadence: BillingCadence;
}>("purchase_plan");
export const purchaseCredits = sendEvent<{ quantity: number }>(
  "purchase_credits"
);
export const cancelPlan = sendEvent("cancel_plan");
export const reactivatePlan = sendEvent("reactivate_plan");
export const addPlanLicenses = sendEvent<{ seats: number }>(
  "add_plan_licenses"
);
export const removePlanLicenses = sendEvent<{ seats: number }>(
  "remove_plan_licenses"
);
export const userPurchaseError = sendEvent<{ message: string }>(
  "user_purchase_error"
);

/**
 * "RSS Links" events
 * */
export const disableRssLinks = sendEvent("disable_rss_links");
export const enableRssLinks = sendEvent("enable_rss_links");
export const copySchedulingLink = sendEvent<{
  source: string;
}>("copy_scheduling_link");
export const enableDefaultLink = sendEvent<{ duration: number }>(
  "enable_default_rss_link"
);
export const disableDefaultLink = sendEvent<{ duration: number }>(
  "disable_default_rss_link"
);
export const createCustomLink = sendEvent("create_custom_rss_link");
export const deleteCustomLink = sendEvent("delete_custom_rss_link");

/**
 * "For You" events
 * */
export const expandTopic = sendEvent("expand_topic");

export const clickClip = sendEvent<{ context: "in_topic" | "to_report" }>(
  "click_clip"
);

/**
 * Workspace Wizard Settings events
 * */
export const workspaceWizard = sendEvent<{ step: number }>("workspace_wizard");

export const workspaceWizardSettings = sendEvent<{
  setting: UpdateCreateWorkspaceSetting;
  oldValue: UpdateCreateWorkspaceSettings[UpdateCreateWorkspaceSetting];
  newValue: UpdateCreateWorkspaceSettings[UpdateCreateWorkspaceSetting];
}>("confirm_workspace_wizard_setting_change");

/**
 * Daily Read events
 * */
export const acknowledgeTTSDisclaimer = sendEvent(
  "tts_disclaimer_acknowledged"
);
export const dismissDailyReadPlayer = sendEvent("dismiss_daily_read_player");
export const playDailyRead = sendEvent("play_daily_read");
export const openDailyReadTranscript = sendEvent("open_daily_read_transcript");
export const shareDailyRead = sendEvent("share_daily_read");

/**
 * Integration Events
 */
export const teamsAdminEnable = sendEvent<{
  status: "pending" | "success" | "error";
}>("teams_admin_enable");

export const featurePopupIntegration = sendEvent<{ provider: string }>(
  "feature_popup_integration"
);

export const showPromotion = sendEvent<{
  promotion: string;
  displayLocation?: string;
}>("show_promotion");
export const dismissPromotion = sendEvent<{
  promotion: string;
}>("dismiss_promotion");
